import { gql, NetworkStatus, useQuery } from '@apollo/client'
import { noop } from 'lodash/fp'
import { toast } from 'presentation/components/Toast'
import { useEffect } from 'react'

export const usePaymentsTable = (): {
  isReady: boolean
  invoices: Invoice[]
  activeSubscription: ActiveSubscription | null
  canFetchMore: boolean
  fetchMore: () => void
  isFetchingMore: boolean
} => {
  const { data, error, networkStatus, ...query } = useQuery<any>(PAYMENTS_TABLE__GET_INVOICES, {
    notifyOnNetworkStatusChange: true,
  })

  const invoiceEdges: InvoiceEdge[] = data?.myEnterprise?.invoices?.edges ?? []
  const invoicesRaw: Invoice[] = invoiceEdges
    .map((edge: InvoiceEdge) => edge?.node)
    .filter((invoice: Invoice | null | undefined): invoice is Invoice => invoice !== undefined && invoice !== null)

  const subscription = data?.myEnterprise?.subscription
  const activeSubscription = subscription?.__typename === 'BillingSubscriptionActive' ? subscription : null

  const invoices: Invoice[] = [
    ...invoicesRaw,
  ]

  const dataHasNextPage = data?.myEnterprise?.invoices?.pageInfo.hasNextPage
  const lastCursor = invoiceEdges.length > 0
    ? invoiceEdges[invoiceEdges.length - 1]?.cursor ?? null
    : null
  const canFetchMore = !!dataHasNextPage && !!lastCursor

  const fetchMore = (): void => {
    query.fetchMore({
      variables: {
        cursor: lastCursor,
      },
    })
      .catch(noop) // error is handled from hook
  }

  // React to errors
  useEffect(() => {
    if (!error) return

    toast({
      id: `useCardTransactons${error.message}`,
      status: 'error',
      title: 'We couldn’t load your payment history.',
      message: error.message,
    })
  }, [error])

  return {
    isReady: !!data,
    invoices,
    activeSubscription,
    canFetchMore,
    fetchMore,
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
  }
}

export const PAYMENTS_TABLE__GET_INVOICES = gql(/* GraphQL */ `
  query PaymentsTable_GetInvoices($cursor: String) {
    myEnterprise {
      id
      subscription {
        ... on BillingSubscriptionInactive {
          id
        }

        ...on BillingSubscriptionActive {
          id
          pastDueInvoice {
            __typename
            id
            createdAt
            createdBy {
              id
              firstname
              lastname
            }
            paidAt
            status
            authorizedAmount
            capturedAmount
            refundedAmount
            linkToPdf
            description
            items {
              amount
              description
            }
          }
          plan {
            id
            name
            price
            product {
              isLegacy
            }
          }

          upcomingInvoice {
            __typename
            description
            paymentDate
            totalAmount
          }

          createdBy {
            id
            firstname
            lastname
          }
        }
      }

      invoices(first: 10, after: $cursor) {
        pageInfo {
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            createdAt
            createdBy {
              id
              firstname
              lastname
            }
            paidAt
            status
            authorizedAmount
            capturedAmount
            refundedAmount
            linkToPdf
            description
          }
        }
      }
    }
  }
`)

export type ActiveSubscription = NonNullable<
  ReturnType<typeof getActiveSubscriptionFromQuery>
>

type Invoice = NonNullable<
  NonNullable<ReturnType<typeof getInvoicesFromQuery>>[number]
>

type InvoiceEdge = {
  cursor?: string | null
  node?: Invoice | null
} | null | undefined

const getActiveSubscriptionFromQuery = (data: any) =>
  data?.myEnterprise?.subscription?.__typename === 'BillingSubscriptionActive'
    ? data.myEnterprise.subscription
    : null

const getInvoicesFromQuery = (data: any) =>
  data?.myEnterprise?.invoices?.edges?.map((edge: any) => edge?.node)
    .filter((n: any) => n !== undefined) || []
